import { ProductSelectEvent } from '@shopify/events';
import { normalizePersonalization, validPersonalization, vinylPreviewColor, artworkMask } from './bullard-personalization-utils.js';

const fontLoads = new Map();

class BullardPersonalization extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.text = this.querySelector('[data-personalization-text]');
    this.font = this.querySelector('[data-personalization-font]');
    this.position = this.querySelector('[data-personalization-position]');
    this.status = this.querySelector('[data-personalization-status]');
    this.figure = this.querySelector('[data-personalization-preview]');
    this.output = this.querySelector('[data-personalization-output]');
    this.canvas = this.querySelector('canvas');
    this.image = this.querySelector('[data-personalization-image]');
    this.layout = this.querySelector('[data-personalization-layout]');
    this.orderText = this.querySelector('[data-personalization-order-text]');
    this.proof = this.querySelector('[data-personalization-proof]');
    this.fonts = JSON.parse(this.querySelector('[data-personalization-fonts]').textContent);
    this.querySelector('fieldset').disabled = false;
    this.querySelector('[data-personalization-unavailable]').hidden = true;
    this.generation = 0;
    this.variantGeneration = 0;
    this.addEventListener('input', () => this.update(), { signal });
    this.addEventListener('change', () => this.update(), { signal });
    document.addEventListener(ProductSelectEvent.eventName, (event) => this.onProductSelect(event), { signal });
    this.observer = new ResizeObserver(() => this.fitText());
    this.observer.observe(this.figure);
    this.update();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.controller = null;
    this.generation++;
  }

  async onProductSelect(event) {
    const section = this.closest('.shopify-section');
    if (!section || !section.contains(event.target)) return;
    const sourceCard = event.target?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;
    const selection = ++this.variantGeneration;
    this.variantPending = true;
    this.generation++;
    if (normalizePersonalization(this.text.value)) {
      this.text.setCustomValidity(this.dataset.loading);
      this.status.textContent = this.dataset.loading;
    }
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || selection !== this.variantGeneration || !detail?.html) return;
      if (detail.productId && String(detail.productId) !== this.dataset.productId) return;
      const replacement = [...detail.html.querySelectorAll('bullard-personalization')].find((el) => el.dataset.productId === this.dataset.productId);
      if (!replacement) return;
      for (const key of ['variantId', 'selectedOptions', 'colorName', 'imageUrl']) this.dataset[key] = replacement.dataset[key];
      if (this.image.getAttribute('src') !== this.dataset.imageUrl) {
        this.image.src = this.dataset.imageUrl;
        this.image.removeAttribute('srcset');
        this.image.removeAttribute('sizes');
      }
    } catch {
      return;
    } finally {
      if (selection === this.variantGeneration && this.isConnected) {
        this.variantPending = false;
        this.update();
      }
    }
  }

  async loadFont() {
    const font = this.fonts.find((entry) => entry.family === this.font.value);
    if (!font) throw new Error('font');
    if (!fontLoads.has(font.family)) {
      const face = new FontFace(font.family, `url(${JSON.stringify(font.url)})`, { display: 'block' });
      document.fonts.add(face);
      fontLoads.set(font.family, face.load().catch((error) => {
        document.fonts.delete(face);
        fontLoads.delete(font.family);
        throw error;
      }));
    }
    await fontLoads.get(font.family);
    return font;
  }

  async prepareArtwork() {
    const url = this.dataset.imageUrl;
    if (this.preparedUrl === url) return this.preparedArtwork;
    this.preparedUrl = url;
    this.preparedArtwork = new Promise((resolve) => {
      const image = new Image();
      const timeout = setTimeout(() => resolve(null), 8000);
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        clearTimeout(timeout);
        try {
          const canvas = document.createElement('canvas');
          canvas.width = image.naturalWidth;
          canvas.height = image.naturalHeight;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(image, 0, 0);
          const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
          const alpha = artworkMask(pixels.data);
          resolve(alpha ? { width: canvas.width, height: canvas.height, alpha } : null);
        } catch {
          resolve(null);
        }
      };
      image.onerror = () => {
        clearTimeout(timeout);
        resolve(null);
      };
      image.src = url;
    });
    return this.preparedArtwork;
  }

  paintArtwork(artwork, color) {
    const canTint = Boolean(artwork);
    this.canvas.hidden = !canTint;
    this.image.hidden = canTint;
    this.figure.style.setProperty('--personalization-ink', canTint ? color : '#171717');
    this.figure.style.setProperty('--personalization-surface', canTint && color === '#f4f4f1' ? '#34383d' : '#f4f1eb');
    if (!canTint || this.lastPaint === `${this.preparedUrl}|${color}`) return;
    this.canvas.width = artwork.width;
    this.canvas.height = artwork.height;
    const context = this.canvas.getContext('2d');
    const pixels = context.createImageData(artwork.width, artwork.height);
    const rgb = [1, 3, 5].map((offset) => parseInt(color.slice(offset, offset + 2), 16));
    for (let index = 0; index < artwork.alpha.length; index++) {
      const pixel = index * 4;
      pixels.data[pixel] = rgb[0];
      pixels.data[pixel + 1] = rgb[1];
      pixels.data[pixel + 2] = rgb[2];
      pixels.data[pixel + 3] = artwork.alpha[index];
    }
    context.putImageData(pixels, 0, 0);
    this.lastPaint = `${this.preparedUrl}|${color}`;
  }

  fitText() {
    if (!this.output || this.figure.hidden || this.output.hidden) return;
    const line = this.output.parentElement;
    this.output.style.fontSize = '56px';
    const width = this.output.getBoundingClientRect().width;
    const available = line.clientWidth;
    if (width > available && available > 0) this.output.style.fontSize = `${Math.max(8, 56 * available / width)}px`;
  }

  async update() {
    const generation = ++this.generation;
    const text = normalizePersonalization(this.text.value);
    const active = Boolean(text);
    this.font.disabled = !active;
    this.position.disabled = !active;
    this.layout.disabled = !active;
    this.orderText.disabled = !active;
    this.proof.disabled = !active;
    this.figure.hidden = !active;
    this.querySelector('[data-personalization-count]').textContent = `${this.text.value.length}/${this.dataset.limit}`;
    this.querySelector('[data-personalization-variant]').textContent = this.dataset.selectedOptions;
    this.text.setCustomValidity('');
    this.text.removeAttribute('aria-invalid');
    if (!active) {
      this.layout.value = '';
      this.orderText.value = '';
      this.status.textContent = '';
      return;
    }
    if (!validPersonalization(this.text.value, Number(this.dataset.limit))) {
      this.status.textContent = this.dataset.charactersError;
      this.text.setCustomValidity(this.dataset.charactersError);
      this.text.setAttribute('aria-invalid', 'true');
      this.output.hidden = true;
      return;
    }
    this.output.hidden = true;
    this.status.textContent = this.dataset.loading;
    this.text.setCustomValidity(this.dataset.loading);
    if (this.variantPending) return;
    try {
      const [font, artwork] = await Promise.all([this.loadFont(), this.prepareArtwork()]);
      if (generation !== this.generation || !this.isConnected) return;
      this.output.textContent = text;
      this.orderText.value = text;
      this.output.style.fontFamily = JSON.stringify(font.family);
      this.output.hidden = false;
      this.figure.dataset.placement = this.position.value === this.position.options[0].value ? 'below' : 'above';
      this.paintArtwork(artwork, vinylPreviewColor(this.dataset.colorName));
      this.layout.value = JSON.stringify({ version: 1, text, font: font.family, placement: this.figure.dataset.placement, alignment: 'center', product: this.dataset.productId, variant: this.dataset.variantId, options: this.dataset.selectedOptions, width: 'Selected width includes the design and added text', proof: 'Written approval before cutting' });
      this.text.setCustomValidity('');
      this.status.textContent = this.dataset.ready;
      requestAnimationFrame(() => this.fitText());
    } catch {
      if (generation !== this.generation || !this.isConnected) return;
      this.status.textContent = this.dataset.fontError;
      this.text.setCustomValidity(this.dataset.fontError);
      this.text.setAttribute('aria-invalid', 'true');
    }
  }
}

if (!customElements.get('bullard-personalization')) customElements.define('bullard-personalization', BullardPersonalization);
